/**
 * English Test Verification Page
 * Students can submit their IELTS/PTE test details for manual verification
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Shield,
  AlertCircle,
  Loader2,
  FileText,
  ExternalLink,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const EnglishTestVerificationPage = () => {
  const { user, token, loading: authLoading } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingVerification, setExistingVerification] = useState(null);
  
  // Form state
  const [testType, setTestType] = useState('');
  const [formData, setFormData] = useState({
    trf_number: '',
    score_report_code: '',
    registration_id: '',
    test_date: '',
    date_of_birth: '',
    candidate_name: '',
    notes: ''
  });
  
  // Scores state
  const [scores, setScores] = useState({
    overall: '',
    listening: '',
    reading: '',
    writing: '',
    speaking: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/uac-desk/login');
      return;
    }
    loadExistingVerification();
  }, [user, authLoading]);

  const loadExistingVerification = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/english-verification/student/${user.user_id}/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.verification) {
          setExistingVerification(data.verification);
        }
      }
    } catch (error) {
      console.error('Error loading verification:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!testType) {
      toast.error('Please select a test type');
      return;
    }
    
    if (testType === 'IELTS' && !formData.trf_number) {
      toast.error('TRF number is required for IELTS');
      return;
    }
    
    if (testType === 'IELTS' && !formData.date_of_birth) {
      toast.error('Date of birth is required for IELTS verification');
      return;
    }
    
    if (testType === 'PTE' && !formData.score_report_code) {
      toast.error('Score Report Code is required for PTE');
      return;
    }
    
    if (!formData.test_date) {
      toast.error('Test date is required');
      return;
    }
    
    if (!scores.overall) {
      toast.error('Please enter your overall score');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        test_type: testType,
        trf_number: formData.trf_number || null,
        score_report_code: formData.score_report_code || null,
        registration_id: formData.registration_id || null,
        test_date: formData.test_date,
        date_of_birth: formData.date_of_birth || null,
        candidate_name: formData.candidate_name || user?.name || null,
        claimed_scores: {
          overall: testType === 'IELTS' ? parseFloat(scores.overall) : parseInt(scores.overall),
          listening: scores.listening ? (testType === 'IELTS' ? parseFloat(scores.listening) : parseInt(scores.listening)) : null,
          reading: scores.reading ? (testType === 'IELTS' ? parseFloat(scores.reading) : parseInt(scores.reading)) : null,
          writing: scores.writing ? (testType === 'IELTS' ? parseFloat(scores.writing) : parseInt(scores.writing)) : null,
          speaking: scores.speaking ? (testType === 'IELTS' ? parseFloat(scores.speaking) : parseInt(scores.speaking)) : null
        },
        notes: formData.notes || null
      };
      
      const res = await fetch(`${API_URL}/api/english-verification/submit?student_id=${user.user_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success('Verification request submitted! Our team will verify your score shortly.');
        loadExistingVerification();
      } else {
        toast.error(data.detail || 'Failed to submit verification request');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit verification request');
    }
    
    setSubmitting(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pending Review</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-8 py-4 w-full">
        <div className="w-full max-w-3xl mx-auto flex items-center gap-3 sm:gap-4">
          <Link to="/uac-desk/dashboard">
            <img 
              src={getLogo()} 
              alt="UniXperts" 
              className="h-7 w-auto object-contain hidden sm:block"
            />
          </Link>
          <div className="h-6 w-px bg-slate-200 hidden sm:block" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/uac-desk/dashboard')}
            className="text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-display">English Test Verification</h1>
            <p className="text-sm text-slate-500">Verify your IELTS or PTE scores</p>
          </div>
        </div>
      </header>

      <main className="w-full max-w-3xl mx-auto p-3 sm:p-4 lg:p-8">
        {/* Existing Verification Status */}
        {existingVerification && (
          <div className={`rounded-2xl p-6 mb-6 border ${
            existingVerification.status === 'pending' ? 'bg-amber-50 border-amber-200' :
            existingVerification.status === 'approved' ? 'bg-emerald-50 border-emerald-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                existingVerification.status === 'pending' ? 'bg-amber-100' :
                existingVerification.status === 'approved' ? 'bg-emerald-100' :
                'bg-red-100'
              }`}>
                {existingVerification.status === 'pending' && <Clock className="w-6 h-6 text-amber-600" />}
                {existingVerification.status === 'approved' && <CheckCircle className="w-6 h-6 text-emerald-600" />}
                {existingVerification.status === 'rejected' && <XCircle className="w-6 h-6 text-red-600" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900">
                    {existingVerification.test_type} Score {existingVerification.status === 'approved' ? 'Verified' : existingVerification.status === 'pending' ? 'Verification Pending' : 'Verification Rejected'}
                  </h3>
                  {getStatusBadge(existingVerification.status)}
                </div>
                
                {existingVerification.status === 'approved' && existingVerification.verified_scores && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{existingVerification.verified_scores.overall}</p>
                      <p className="text-xs text-slate-500">Overall</p>
                    </div>
                    {existingVerification.verified_scores.listening && (
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-lg font-semibold text-slate-700">{existingVerification.verified_scores.listening}</p>
                        <p className="text-xs text-slate-500">Listening</p>
                      </div>
                    )}
                    {existingVerification.verified_scores.reading && (
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-lg font-semibold text-slate-700">{existingVerification.verified_scores.reading}</p>
                        <p className="text-xs text-slate-500">Reading</p>
                      </div>
                    )}
                    {existingVerification.verified_scores.writing && (
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-lg font-semibold text-slate-700">{existingVerification.verified_scores.writing}</p>
                        <p className="text-xs text-slate-500">Writing</p>
                      </div>
                    )}
                    {existingVerification.verified_scores.speaking && (
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-lg font-semibold text-slate-700">{existingVerification.verified_scores.speaking}</p>
                        <p className="text-xs text-slate-500">Speaking</p>
                      </div>
                    )}
                  </div>
                )}
                
                {existingVerification.status === 'pending' && (
                  <p className="text-sm text-amber-700 mt-2">
                    Your verification request is being reviewed. This usually takes 1-2 business days.
                  </p>
                )}
                
                {existingVerification.status === 'rejected' && (
                  <div className="mt-2">
                    <p className="text-sm text-red-700">
                      Your verification was not approved. {existingVerification.counselor_notes && `Reason: ${existingVerification.counselor_notes}`}
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      You can submit a new verification request below.
                    </p>
                  </div>
                )}
                
                {existingVerification.verified_at && existingVerification.status === 'approved' && (
                  <p className="text-xs text-slate-500 mt-2">
                    Verified on {new Date(existingVerification.verified_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Show form only if no pending verification */}
        {(!existingVerification || existingVerification.status !== 'pending') && (
          <>
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900">Secure Score Verification</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    We verify your IELTS/PTE scores using official public portals. Your credentials are used only for verification and are never stored.
                  </p>
                  <div className="flex gap-4 mt-3 text-xs">
                    <a 
                      href="https://ielts.idp.com/results/check-your-result" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> IELTS Portal
                    </a>
                    <a 
                      href="https://srw.pteacademic.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> PTE Portal
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-ux-blue" />
                Submit Verification Request
              </h2>

              {/* Test Type Selection */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700">Test Type *</Label>
                <Select value={testType} onValueChange={setTestType}>
                  <SelectTrigger className="mt-1" data-testid="test-type-select">
                    <SelectValue placeholder="Select IELTS or PTE" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IELTS">IELTS (Academic)</SelectItem>
                    <SelectItem value="PTE">PTE Academic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional Fields based on Test Type */}
              {testType === 'IELTS' && (
                <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-medium text-slate-800">IELTS Verification Details</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-sm text-slate-600">TRF Number *</Label>
                      <Input
                        placeholder="e.g., 25IN123456SMITHJ001A"
                        value={formData.trf_number}
                        onChange={(e) => setFormData({ ...formData, trf_number: e.target.value })}
                        className="mt-1"
                        data-testid="trf-number-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Found on your Test Report Form</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Date of Birth *</Label>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        className="mt-1"
                        data-testid="dob-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {testType === 'PTE' && (
                <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-medium text-slate-800">PTE Verification Details</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-sm text-slate-600">Score Report Code *</Label>
                      <Input
                        placeholder="e.g., ABC123XYZ"
                        value={formData.score_report_code}
                        onChange={(e) => setFormData({ ...formData, score_report_code: e.target.value })}
                        className="mt-1"
                        data-testid="score-report-code-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Found in your PTE score report</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Registration ID (Optional)</Label>
                      <Input
                        placeholder="e.g., 12345678"
                        value={formData.registration_id}
                        onChange={(e) => setFormData({ ...formData, registration_id: e.target.value })}
                        className="mt-1"
                        data-testid="registration-id-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Common Fields */}
              {testType && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2 mb-6">
                    <div>
                      <Label className="text-sm text-slate-600">Test Date *</Label>
                      <Input
                        type="date"
                        value={formData.test_date}
                        onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
                        className="mt-1"
                        max={new Date().toISOString().split('T')[0]}
                        data-testid="test-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Name on Certificate</Label>
                      <Input
                        placeholder="As it appears on your certificate"
                        value={formData.candidate_name}
                        onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })}
                        className="mt-1"
                        data-testid="candidate-name-input"
                      />
                    </div>
                  </div>

                  {/* Scores Section */}
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">Your Scores *</Label>
                    <div className="grid gap-3 sm:grid-cols-5">
                      <div>
                        <Label className="text-xs text-slate-500">Overall *</Label>
                        <Input
                          type="number"
                          step={testType === 'IELTS' ? '0.5' : '1'}
                          min={testType === 'IELTS' ? '0' : '10'}
                          max={testType === 'IELTS' ? '9' : '90'}
                          placeholder={testType === 'IELTS' ? '6.5' : '65'}
                          value={scores.overall}
                          onChange={(e) => setScores({ ...scores, overall: e.target.value })}
                          className="mt-1"
                          data-testid="overall-score-input"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Listening</Label>
                        <Input
                          type="number"
                          step={testType === 'IELTS' ? '0.5' : '1'}
                          min={testType === 'IELTS' ? '0' : '10'}
                          max={testType === 'IELTS' ? '9' : '90'}
                          placeholder={testType === 'IELTS' ? '6.5' : '65'}
                          value={scores.listening}
                          onChange={(e) => setScores({ ...scores, listening: e.target.value })}
                          className="mt-1"
                          data-testid="listening-score-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Reading</Label>
                        <Input
                          type="number"
                          step={testType === 'IELTS' ? '0.5' : '1'}
                          min={testType === 'IELTS' ? '0' : '10'}
                          max={testType === 'IELTS' ? '9' : '90'}
                          placeholder={testType === 'IELTS' ? '6.0' : '60'}
                          value={scores.reading}
                          onChange={(e) => setScores({ ...scores, reading: e.target.value })}
                          className="mt-1"
                          data-testid="reading-score-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Writing</Label>
                        <Input
                          type="number"
                          step={testType === 'IELTS' ? '0.5' : '1'}
                          min={testType === 'IELTS' ? '0' : '10'}
                          max={testType === 'IELTS' ? '9' : '90'}
                          placeholder={testType === 'IELTS' ? '6.0' : '58'}
                          value={scores.writing}
                          onChange={(e) => setScores({ ...scores, writing: e.target.value })}
                          className="mt-1"
                          data-testid="writing-score-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Speaking</Label>
                        <Input
                          type="number"
                          step={testType === 'IELTS' ? '0.5' : '1'}
                          min={testType === 'IELTS' ? '0' : '10'}
                          max={testType === 'IELTS' ? '9' : '90'}
                          placeholder={testType === 'IELTS' ? '6.5' : '62'}
                          value={scores.speaking}
                          onChange={(e) => setScores({ ...scores, speaking: e.target.value })}
                          className="mt-1"
                          data-testid="speaking-score-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {testType === 'IELTS' ? 'IELTS scores range from 0-9 (in 0.5 increments)' : 'PTE scores range from 10-90'}
                    </p>
                  </div>

                  {/* Notes */}
                  <div className="mb-6">
                    <Label className="text-sm text-slate-600">Additional Notes (Optional)</Label>
                    <Textarea
                      placeholder="Any additional information..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="mt-1"
                      rows={3}
                      data-testid="notes-textarea"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-ux-blue hover:bg-ux-blue/90"
                    disabled={submitting}
                    data-testid="submit-verification-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Submit for Verification
                      </>
                    )}
                  </Button>
                </>
              )}
            </form>

            {/* Help Section */}
            <div className="mt-6 text-center text-sm text-slate-500">
              <p>
                Need help? <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="text-ux-blue hover:underline">Contact support</a>
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default EnglishTestVerificationPage;
